import React, { useState, useEffect } from 'react';
import '../styles/pages/Players.scss';
import '../styles/components/index.scss';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Helmet } from 'react-helmet';

const Contact = () => {
  // State for form fields
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    clubName: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      console.log('Submitting form to /api/contact.php');
      // Make API request
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Success
        console.log('Form submitted successfully');
        setSubmitStatus('success');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          clubName: '',
          email: '',
          message: ''
        });
      } else {
        // Error
        console.error('Form submission error:', response.status);
        const errorData = await response.json();
        setSubmitStatus('error');
        if (errorData.errors) {
          setErrors(errorData.errors);
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Set page title
  useEffect(() => {
    document.title = "Contact Us - FirstWhistle";
  }, []);

  return (
    <div className="players-page contact-page">
      <Helmet>
        <title>Contact FirstWhistle | Get in Touch with Our Team</title>
        <meta name="description" content="Have questions about FirstWhistle? Get in touch with our team. We're here to help with any inquiries about our football team management app." />
        <meta property="og:title" content="Contact FirstWhistle | Get in Touch with Our Team" />
        <meta property="og:description" content="Have questions about FirstWhistle? Get in touch with our team. We're here to help with any inquiries about our football team management app." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/contact" />
        <link rel="canonical" href="https://firstwhistle.app/contact" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ContactPage",
              "name": "Contact FirstWhistle",
              "description": "Have questions about FirstWhistle? Get in touch with our team. We're here to help with any inquiries about our football team management app.",
              "mainEntity": {
                "@type": "Organization",
                "name": "FirstWhistle",
                "url": "https://firstwhistle.app",
                "logo": "https://firstwhistle.app/static/media/logo_white-01.svg",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "customer support",
                  "url": "https://firstwhistle.app/contact"
                },
                "sameAs": [
                  "https://www.instagram.com/firstwhistle.app",
                  "https://www.facebook.com/FirstWhistleApp"
                ]
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section players-hero-section contact-hero-section">
        <div className="hero-content players-hero-content">
          <h1 className="players-page-h1">Get in Touch</h1>
          <p className="subtext players-page-subtext">Have questions about FirstWhistle? We're here to help. Drop us a message and our team will get back to you.</p>
        </div>
        <div className="hero-background">
          <img src={require('../images/contact-hero-bg.jpeg')} alt="Football field" className="players-hero-image" />
          <div className="overlay"></div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="contact-info">
            <h2>Contact Information</h2>
            <p>Fill out the form and our team will get back to you within 24 hours.</p>
            
            <div className="contact-methods">
              <div className="contact-method">
                <PhoneIcon />
                <span>07875 930733</span>
              </div>
              <div className="contact-method">
                <LocationOnIcon />
                <span>Hastings, East Sussex</span>
              </div>
              <div className="contact-social">
                <p>Or follow us on social media:</p>
                <div className="social-icons">
                  <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                    <InstagramIcon className="social-icon" style={{ color: '#ffffff' }} />
                  </a>
                  <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                    <FacebookIcon className="social-icon" style={{ color: '#ffffff' }} />
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-container">
            <h2>Send Us a Message</h2>
            
            {submitStatus === 'success' && (
              <div className="success-message">
                <CheckCircleOutlineIcon />
                <p>Your message has been sent successfully. Thank you for contacting us!</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="error-message">
                <ErrorOutlineIcon />
                <p>There was an error sending your message. Please try again later.</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group tight-field">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && <span className="error-text">{errors.firstName}</span>}
                </div>
                
                <div className="form-group tight-field">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Smith"
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && <span className="error-text">{errors.lastName}</span>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group tight-field">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john.smith@example.com"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
                
                <div className="form-group tight-field">
                  <label htmlFor="clubName">Club Name</label>
                  <input
                    type="text"
                    id="clubName"
                    name="clubName"
                    value={formData.clubName}
                    onChange={handleChange}
                    placeholder="Manchester FC"
                  />
                </div>
              </div>
              
              <div className="form-group tight-field">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="How can we help you?"
                  rows="5"
                  className={errors.message ? 'error' : ''}
                ></textarea>
                {errors.message && <span className="error-text">{errors.message}</span>}
              </div>
              
              <div className="form-actions">
                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-container">
          <div className="faq-item">
            <div className="faq-question">
              <h3>When will FirstWhistle be available?</h3>
            </div>
            <div className="faq-answer expanded">
              <p>FirstWhistle is currently in development. Sign up today to be among the first to access the platform when we launch and receive exclusive early-bird offers.</p>
            </div>
          </div>
          <div className="faq-item">
            <div className="faq-question">
              <h3>How can I become a beta tester?</h3>
            </div>
            <div className="faq-answer expanded">
              <p>We're looking for passionate football coaches, players and parents to join our beta testing programme. Please fill out the contact form above with "Beta Testing" in your message to express interest.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <p className="tagline">FirstWhistle – The intelligent football team manager.</p>
          </div>
          <div className="footer-navigation">
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/players">Players</Link>
              <Link to="/coaches">Coaches</Link>
              <Link to="/parents">Parents</Link>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-socials">
              <a href="https://www.instagram.com/firstwhistle.app" target="_blank" rel="noopener noreferrer">
                <InstagramIcon className="social-icon" style={{ color: '#ffffff' }} />
              </a>
              <a href="https://www.facebook.com/FirstWhistleApp" target="_blank" rel="noopener noreferrer">
                <FacebookIcon className="social-icon" style={{ color: '#ffffff' }} />
              </a>
            </div>
          </div>
          <div className="footer-legal">
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact; 